import { useState, type MouseEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Avatar, Box, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
//
import { useAuth } from '../../contexts/AuthContext';
import { PATH_DASHBOARD, PATH_PAGE } from '../../routes/paths';

// ----------------------------------------------------------------------

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

// ----------------------------------------------------------------------

export default function AccountMenu() {
  const { account, isEmployer, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!account) return null;

  const dashboard = isAdmin ? PATH_DASHBOARD.admin.root : isEmployer ? PATH_DASHBOARD.employer.root : PATH_DASHBOARD.employee.root;
  const profile = isAdmin ? PATH_DASHBOARD.admin.settings : isEmployer ? PATH_DASHBOARD.employer.profile : PATH_DASHBOARD.employee.profile;

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleClose();
    logout();
    navigate(PATH_PAGE.root);
  };

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ p: 0.5 }} aria-label="Account menu">
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
          {initials(account.name)}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {account.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {account.email}
          </Typography>
          <Typography variant="caption" sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
            {isAdmin ? 'Super admin' : isEmployer ? 'Employer' : 'Job seeker'}
          </Typography>
        </Box>

        <Divider />

        <MenuItem component={RouterLink} to={dashboard} onClick={handleClose}>
          Dashboard
        </MenuItem>
        <MenuItem component={RouterLink} to={profile} onClick={handleClose}>
          Profile
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
